import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import {
  currentSubscriptionEndpoint,
  detectPushSupport,
  subscribeForPush,
  unsubscribeFromPush,
} from '@/lib/push';

const CATEGORIES = [
  'tracking_reminder',
  'session_reminder',
  'milestone',
  'coach_message',
  'sealed_letter',
  'gate_result',
  'weekly_review',
] as const;
const CHANNELS = ['email', 'in_app', 'push'] as const;

type Category = (typeof CATEGORIES)[number];
type Channel = (typeof CHANNELS)[number];

type Matrix = Map<string, boolean>;
const key = (category: Category, channel: Channel): string => `${category}:${channel}`;

export function SettingsNotificationsPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<Matrix>(new Map());
  const [original, setOriginal] = useState<Matrix>(new Map());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [pushSupport] = useState(() => detectPushSupport());
  const [pushEndpoint, setPushEndpoint] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  useEffect(() => {
    if (!pushSupport.supported) return;
    void currentSubscriptionEndpoint().then((endpoint) => setPushEndpoint(endpoint));
  }, [pushSupport.supported]);

  const handlePushToggle = async (enable: boolean): Promise<void> => {
    if (!user) return;
    setPushBusy(true);
    setPushError(null);
    try {
      if (enable) {
        const endpoint = await subscribeForPush(accessToken);
        setPushEndpoint(endpoint);
      } else {
        await unsubscribeFromPush(accessToken);
        setPushEndpoint(null);
      }
    } catch (cause) {
      setPushError(cause instanceof Error ? cause.message : 'push_error');
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        const result = await api.getNotificationPreferences(accessToken);
        if (cancelled) return;
        const next: Matrix = new Map();
        for (const entry of result.preferences) {
          next.set(key(entry.category, entry.channel), entry.enabled);
        }
        setMatrix(next);
        setOriginal(new Map(next));
        setError(null);
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : t('common.errors.network'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, t]);

  const hasChanges = (() => {
    if (matrix.size !== original.size) return true;
    for (const [k, v] of matrix) {
      if (original.get(k) !== v) return true;
    }
    return false;
  })();

  const toggle = (category: Category, channel: Channel): void => {
    const next = new Map(matrix);
    next.set(key(category, channel), !matrix.get(key(category, channel)));
    setMatrix(next);
  };

  const allOn = (channel: Channel): void => {
    const next = new Map(matrix);
    for (const cat of CATEGORIES) next.set(key(cat, channel), true);
    setMatrix(next);
  };

  const allOff = (channel: Channel): void => {
    const next = new Map(matrix);
    for (const cat of CATEGORIES) next.set(key(cat, channel), false);
    setMatrix(next);
  };

  const save = async (): Promise<void> => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const preferences: api.NotificationPreferenceEntry[] = [];
      for (const category of CATEGORIES) {
        for (const channel of CHANNELS) {
          preferences.push({
            category,
            channel,
            enabled: matrix.get(key(category, channel)) ?? false,
          });
        }
      }
      const result = await api.updateNotificationPreferences(accessToken, { preferences });
      const next: Matrix = new Map();
      for (const entry of result.preferences) {
        next.set(key(entry.category, entry.channel), entry.enabled);
      }
      setMatrix(next);
      setOriginal(new Map(next));
      setSavedAt(new Date());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.errors.network'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <h1 className="font-display text-display-lg text-ink">
        {t('settings.notifications.title')}
      </h1>
      <p className="mt-s2 text-sm text-gray-2">{t('settings.notifications.hint')}</p>

      {loading && (
        <p className="mt-s5 text-sm text-gray-2" data-testid="prefs-loading">
          {t('common.labels.loading')}
        </p>
      )}

      {error && (
        <div className="mt-s5 rounded-md border border-flame-amber bg-flame-glow p-s3 text-sm text-flame-ember">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="mt-s6 overflow-x-auto rounded-lg border border-bone-3 bg-bone-2">
            <table className="w-full text-left text-sm">
              <thead className="bg-bone-3">
                <tr>
                  <th className="px-s3 py-s2 font-medium text-ink">
                    {t('settings.notifications.column.category')}
                  </th>
                  {CHANNELS.map((channel) => (
                    <th key={channel} className="px-s3 py-s2 font-medium text-ink text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span>{t(`settings.notifications.channel.${channel}`)}</span>
                        <div className="flex gap-1 text-xs text-gray-2">
                          <button
                            type="button"
                            className="underline hover:text-flame-ember"
                            onClick={() => allOn(channel)}
                            data-testid={`prefs-all-on-${channel}`}
                          >
                            {t('settings.notifications.allOn')}
                          </button>
                          <span>·</span>
                          <button
                            type="button"
                            className="underline hover:text-flame-ember"
                            onClick={() => allOff(channel)}
                            data-testid={`prefs-all-off-${channel}`}
                          >
                            {t('settings.notifications.allOff')}
                          </button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((category) => (
                  <tr key={category} className="border-t border-bone-3">
                    <td className="px-s3 py-s2">
                      <div className="font-medium text-ink">
                        {t(`settings.notifications.category.${category}.label`)}
                      </div>
                      <div className="text-xs text-gray-2">
                        {t(`settings.notifications.category.${category}.hint`)}
                      </div>
                    </td>
                    {CHANNELS.map((channel) => (
                      <td key={channel} className="px-s3 py-s2 text-center">
                        <input
                          type="checkbox"
                          className="h-5 w-5 cursor-pointer accent-flame-ember"
                          checked={matrix.get(key(category, channel)) ?? false}
                          onChange={() => toggle(category, channel)}
                          data-testid={`prefs-${category}-${channel}`}
                          aria-label={t(`settings.notifications.cell.aria`, {
                            category: t(`settings.notifications.category.${category}.label`),
                            channel: t(`settings.notifications.channel.${channel}`),
                          })}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-s5 flex items-center gap-s3">
            <button
              type="button"
              className="rounded-md bg-flame-ember px-s4 py-s2 font-medium text-bone-1 disabled:opacity-50"
              onClick={() => void save()}
              disabled={!hasChanges || saving}
              data-testid="prefs-save"
            >
              {saving
                ? t('common.actions.saving')
                : t('common.actions.save')}
            </button>
            {savedAt && !saving && !hasChanges && (
              <span className="text-sm text-gray-2" data-testid="prefs-saved-banner">
                {t('settings.notifications.savedAt', {
                  time: savedAt.toLocaleTimeString(),
                })}
              </span>
            )}
          </div>

          {pushSupport.supported && (
            <div
              className="mt-s6 rounded-lg border border-bone-3 bg-bone-2 p-s4"
              data-testid="push-section"
            >
              <h2 className="font-display text-lg text-ink">
                {t('settings.notifications.push.title')}
              </h2>
              <p className="mt-s2 text-sm text-gray-2">
                {t('settings.notifications.push.hint')}
              </p>
              <div className="mt-s3 flex items-center gap-s3">
                {pushEndpoint ? (
                  <button
                    type="button"
                    className="rounded-md border border-flame-ember px-s4 py-s2 text-flame-ember disabled:opacity-50"
                    onClick={() => void handlePushToggle(false)}
                    disabled={pushBusy}
                    data-testid="push-disable"
                  >
                    {t('settings.notifications.push.disable')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rounded-md bg-flame-ember px-s4 py-s2 font-medium text-bone-1 disabled:opacity-50"
                    onClick={() => void handlePushToggle(true)}
                    disabled={pushBusy || pushSupport.permission === 'denied'}
                    data-testid="push-enable"
                  >
                    {t('settings.notifications.push.enable')}
                  </button>
                )}
                {pushSupport.permission === 'denied' && (
                  <span className="text-sm text-flame-ember" data-testid="push-blocked">
                    {t('settings.notifications.push.blocked')}
                  </span>
                )}
                {pushEndpoint && (
                  <span className="text-sm text-gray-2" data-testid="push-status">
                    {t('settings.notifications.push.subscribed')}
                  </span>
                )}
              </div>
              {pushError && (
                <p className="mt-s2 text-sm text-flame-ember" data-testid="push-error">
                  {pushError}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
